import { Button, Css, ModalBody, ModalFooter, ModalHeader, useModal } from "@homebound/beam";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  TakeoffsUsageModalQuery,
  useTakeoffsUsageModalQuery,
} from "src/routes/libraries/material-catalog/components/takeoffs-usage-modal/TakeoffsUsageModal.generated";
import { createPlanPackageTakeoffUrl } from "src/RouteUrls";
import { queryResult } from "src/utils";

type TakeoffsUsageModalProps = { materialVariantId: string };

type TakeoffUsageReadyPlan = TakeoffsUsageModalQuery["materialVariant"]["takeoffUsages"][number];

export function TakeoffsUsageModal(props: TakeoffsUsageModalProps) {
  const { materialVariantId } = props;
  const { closeModal } = useModal();
  const query = useTakeoffsUsageModalQuery({ variables: { id: materialVariantId } });

  return (
    <>
      <ModalHeader>Takeoffs Usage</ModalHeader>
      <ModalBody>
        {queryResult(query, {
          data: (data) => <TakeoffsUsageList takeoffUsages={data.materialVariant.takeoffUsages} />,
        })}
      </ModalBody>
      <ModalFooter>
        <Button label="Close" onClick={closeModal} />
      </ModalFooter>
    </>
  );
}

type TakeoffsUsageListProps = { takeoffUsages: TakeoffsUsageModalQuery["materialVariant"]["takeoffUsages"] };

function TakeoffsUsageList(props: TakeoffsUsageListProps) {
  const { takeoffUsages } = props;
  const sections = useMemo(
    () =>
      [
        {
          title: "Plan Packages",
          testId: "planPackages",
          items: takeoffUsages
            .filter((rp) => rp.__typename === "PlanPackage")
            .sortBy((rp) => rp.displayName ?? rp.name),
        },
        {
          title: "Design Packages",
          testId: "designPackages",
          items: takeoffUsages
            .filter((rp) => rp.__typename === "DesignPackage")
            .sortBy((rp) => rp.displayName ?? rp.name),
        },
        {
          title: "Product Offerings",
          testId: "productOfferings",
          items: takeoffUsages
            .filter((rp) => rp.__typename === "ProductOffering")
            .sortBy((rp) => rp.displayName ?? rp.name),
        },
      ].filter((section) => section.items.nonEmpty),
    [takeoffUsages],
  );

  if (sections.isEmpty) {
    return <div css={Css.gray700.$}>Not used in any takeoffs.</div>;
  }

  return (
    <div css={Css.df.fdc.gap3.$} data-testid="takeoffsUsageList">
      {sections.map((section) => (
        <TakeoffsUsageSection
          key={section.testId}
          title={section.title}
          testId={section.testId}
          items={section.items}
        />
      ))}
    </div>
  );
}

type TakeoffsUsageSectionProps = { title: string; testId: string; items: TakeoffUsageReadyPlan[] };

function TakeoffsUsageSection(props: TakeoffsUsageSectionProps) {
  const { title, testId, items } = props;
  return (
    <div data-testid={testId}>
      <div css={Css.gray700.mb1.$}>{title}</div>
      <ul css={Css.m0.pl2.$}>
        {items.map((rp) => (
          <li key={rp.id} css={Css.mb1.$} data-testid="takeoffsUsageItem">
            <TakeoffUsageLink readyPlan={rp} />
          </li>
        ))}
      </ul>
    </div>
  );
}

type TakeoffUsageLinkProps = { readyPlan: TakeoffUsageReadyPlan };

function TakeoffUsageLink(props: TakeoffUsageLinkProps) {
  const { readyPlan } = props;
  const label = readyPlan.displayName ?? readyPlan.name;
  const to = getTakeoffUsageUrl(readyPlan);
  if (!to) {
    return <span>{label}</span>;
  }
  return (
    <Link to={to} target="_blank">
      {label}
    </Link>
  );
}

function getTakeoffUsageUrl(readyPlan: TakeoffUsageReadyPlan): string | undefined {
  if (readyPlan.__typename === "PlanPackage") {
    // Plan Packages also have a blueprintURL; but it takes you to the plan overview
    // This will take the user straight to the takeoff manager to reduce an extra click
    return createPlanPackageTakeoffUrl(readyPlan.id, readyPlan.aggregateLatest.id);
  }
  if (readyPlan.__typename === "DesignPackage" || readyPlan.__typename === "ProductOffering") {
    return readyPlan.blueprintUrl.path;
  }
  return undefined;
}
